import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import { setRgAddress } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import getExtraInfoOrMeta from '@rsos/utils/getExtraInfoOrMeta';
import { reverseGeocodeLocationToAddress } from './helpers';

/**
 * Reverse geocode hook that determines which reverse geocode service to use then stores the reverse
 * geocoded address and addressObject in Redux. If it is a premium account, the location
 * will be reverse geocoded through the GIS geocode service url if available, otherwise use the
 * default Esri geocode service url. If it is a non-premium account, use the Google reverse
 * geocode service.
 * @param {Object} params
 * @param {Boolean} params.isSecondaryLocation - is it a 911 event secondary (ANI/ALI) location?
 * @param {Object} params.location - 911 event's location data
 * @param {Number} params.locationIndex - 911 event's location index
 * @param {String} params.service - Which reverse geocoding service to use
 */
const useReverseGeocode = ({
  isSecondaryLocation,
  location,
  locationIndex,
  service,
}) => {
  const dispatch = useDispatch();

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const gisGeocodeService = useSelector(selectGeocodeService);

  const gisGeocodeServiceUrl = gisGeocodeService?.gis_service_url || null;

  const gisGeocodeServiceToken = gisGeocodeService?.token || null;

  const callerID = location.caller_id;

  const latitude = parseFloat(location.latitude);

  const longitude = parseFloat(location.longitude);

  const locationTime = location.location_time;

  const hasLocationChanged = !!(
    callerID &&
    latitude &&
    longitude &&
    locationTime
  );

  const hasSinglelineAddress = !!(
    location?.address_single_line && location.address_single_line.trim() !== ''
  );

  const hasReverseGeocodeAddress = !!location?.rgAddress;

  const { payloadID, requestID } = getExtraInfoOrMeta(location) || {};

  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

  const [foundAddress, setFoundAddress] = useState(null);

  // Reverse geocode a location's coordinates to a formatted address & addressObject.
  useEffect(() => {
    if (
      latitude &&
      longitude &&
      hasLocationChanged &&
      !hasReverseGeocodeAddress &&
      !hasSinglelineAddress &&
      service
    ) {
      const location = {
        latitude,
        longitude,
        onReverseGeocoding: setIsReverseGeocoding,
        service,
        serviceUrl: gisGeocodeServiceUrl,
        token: gisGeocodeServiceToken,
      };

      reverseGeocodeLocationToAddress(location)
        .then(response =>
          response
            ? setFoundAddress({ ...response, callerID })
            : setFoundAddress(null),
        )
        .catch(() => {
          setFoundAddress(null);
        });
    }
  }, [
    callerID,
    gisGeocodeServiceUrl,
    gisGeocodeServiceToken,
    hasLocationChanged,
    hasReverseGeocodeAddress,
    hasSinglelineAddress,
    latitude,
    longitude,
    service,
  ]);

  // If reverse geocode returns an address & address object, dispatch it to emergencyCalls reducer.
  useEffect(() => {
    if (foundAddress) {
      const {
        address,
        addressObject,
        rgAddressProvider,
        callerID: foundCallerID,
      } = foundAddress;

      // Only dispatch if the callerID from when the request was made matches
      // the current callerID. This prevents race conditions when the user
      // switches calls before the geocode request completes.
      if (foundCallerID !== callerID) {
        setFoundAddress(null);
        return;
      }

      dispatch(
        setRgAddress({
          address,
          addressObject,
          callerID,
          isSecondaryLocation,
          locationIndex,
          rgAddressProvider,
        }),
      );

      setFoundAddress(null);
    }
  }, [
    callerID,
    dispatch,
    foundAddress,
    locationIndex,
    isSecondaryLocation,
    irpVersion,
    payloadID,
    requestID,
  ]);

  return { isReverseGeocoding };
};

useReverseGeocode.propTypes = {
  isSecondaryLocation: PropTypes.bool,
  location: PropTypes.shape({
    caller_id: PropTypes.string.isRequired,
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    locationTime: PropTypes.number.isRequired,
  }).isRequired,
  locationIndex: PropTypes.number,
  service: PropTypes.string,
};

export default useReverseGeocode;
